import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@mlm/ui';
import { getTenantFeatures } from '../../lib/api';

/**
 * Accounting preview shell bits.
 *
 * These pages render demo fixtures (see sample-data.ts) so the accounting
 * area can be walked through before the module is built. They are reachable
 * only while the tenant's `accounting_enabled` flag is on.
 */

/** Redirects to the dashboard when the Accounting preview is switched off. */
export function RequireAccounting({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
    staleTime: 60_000,
    retry: false,
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !data?.accounting_enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">{title}</h1>
          <Badge variant="secondary">Preview</Badge>
        </div>
        {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      {actions}
    </div>
  );
}

/** Right-aligned monospace money cell — matches the reports tables. */
export function Money({ children }: { children: ReactNode }) {
  return <span className="font-mono tabular-nums">{children}</span>;
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{children}</p>;
}
